import { useEffect, useState } from 'react';
import { Clock3, VolumeX, ShieldCheck } from 'lucide-react';
import SectionHead from './SectionHead';
import { Reveal, Stagger, Item } from '../lib/motion';

const LOOP = [...Array(11).fill('markt'), ...Array(7).fill('partner')];

const STATS = [
  { value: '260\u202F×', label: 'läuft Ihr Spot pro Tag auf jedem Bildschirm – bei 13 Stunden Öffnungszeit' },
  { value: '13.000\u202F×', label: 'pro Tag über alle 50 Bildschirme' },
  { value: '390.000\u202F×', label: 'pro Monat – an sechs Tagen die Woche' },
];

const FEATURES = [
  { icon: Clock3, title: 'Zeitfenster nach Wunsch', text: 'Frühstück, Mittag, Feierabend oder Wochenende: Auf Wunsch läuft Ihr Spot nur dann, wenn Ihre Kunden im Markt sind.' },
  { icon: VolumeX, title: 'Bild wirkt, Ton stört nicht', text: 'Die Bildschirme laufen ohne Ton. Gute Spots arbeiten mit einem starken Bild, einem Satz und einem Preis oder Angebot.' },
  { icon: ShieldCheck, title: 'Eine Branche, ein Partner', text: 'Mit Branchenexklusivität bleibt Ihr Wettbewerber draußen – für die gesamte Laufzeit Ihres Auftrags.' },
];

export default function MechanicsSection() {
  const [head, setHead] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setHead((h) => (h + 1) % LOOP.length), 700);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="schleife" data-testid="mechanics-section" className="chapter">
      <div className="wrap">
        <SectionHead
          n="03"
          eyebrow="Die Schleife"
          title="Drei Minuten. Achtzehn Plätze. Höchstens sieben Partner."
          testid="mechanics-headline"
          lead="Alle Bildschirme zeigen eine Schleife von drei Minuten: Angebote, Frische, Rezepte und Regionales im Wechsel mit Partner-Spots. Die Zahl der Partner ist bewusst begrenzt – damit jeder Spot Gewicht hat."
        />

        <Reveal className="mt-16 rounded-2xl border border-[#282C37] bg-[#16181D] p-6 sm:p-10" data-testid="mechanics-loop">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#7D8391]">3-Minuten-Schleife · 1 Platz = 10 Sekunden</p>
            <div className="flex flex-wrap gap-5 font-mono-num text-[10px] uppercase tracking-widest">
              <span className="flex items-center gap-2 text-[#B6BCC8]"><span className="h-2.5 w-2.5 rounded-sm bg-[#3A3F4B]" /> 11 Plätze Markt</span>
              <span className="flex items-center gap-2 text-[#FF6B00]"><span className="h-2.5 w-2.5 rounded-sm bg-[#FF6B00]" /> bis zu 7 Plätze Partner</span>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-9 gap-1.5 sm:grid-cols-18" data-testid="mechanics-loop-bar">
            {LOOP.map((t, i) => {
              const on = i === head;
              return (
                <div
                  key={`slot-${i}-${t}`}
                  className={`h-10 rounded-md transition-[transform,opacity,box-shadow] duration-300 sm:h-14 ${
                    t === 'partner' ? 'bg-[#FF6B00]' : 'bg-[#3A3F4B]'
                  } ${on ? 'scale-y-110 shadow-lg shadow-[#FF6B00]/30 opacity-100' : 'opacity-70'}`}
                />
              );
            })}
          </div>
          <div className="mt-3 flex justify-between font-mono-num text-[10px] text-[#7D8391]">
            <span>0:00</span><span>1:30</span><span>3:00</span>
          </div>
        </Reveal>

        <Stagger className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-[#282C37] bg-[#282C37] sm:grid-cols-3">
          {STATS.map((s, i) => (
            <Item key={s.value} className="bg-[#0F1012] p-8" data-testid={`mechanics-stat-${i}`}>
              <p className="font-mono-num text-4xl font-bold tracking-tighter text-[#FF6B00] sm:text-5xl">{s.value}</p>
              <p className="t-body mt-3 max-w-[28ch]">{s.label}</p>
            </Item>
          ))}
        </Stagger>

        <div className="mt-16 divide-y divide-[#282C37] border-y border-[#282C37]">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.06} className="grid gap-4 py-8 sm:grid-cols-12 sm:gap-8" data-testid={`mechanics-feature-${i}`}>
              <div className="sm:col-span-1"><f.icon className="h-6 w-6 text-[#FF6B00]" /></div>
              <h3 className="font-display text-xl font-bold sm:col-span-4">{f.title}</h3>
              <p className="t-body sm:col-span-7 max-w-[60ch]">{f.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
